import React, { useCallback, useMemo } from 'react';

import { CatalogRoom, ConflictResolution, StopsellRoom } from '~/types/stopsell';

import { findRateConflict, sliceResolutionsByRoom } from '~/utils/stopsellConflicts';

import StopsellRoomGroup from './StopsellRoomGroup';

interface Props {
  roomIndex: number;
  room: StopsellRoom;
  selected: Array<boolean>;
  catalogRooms: Array<CatalogRoom>;
  selectedRoomTypeId: string;
  resolutions: Record<string, ConflictResolution>;
  onDateChange: (roomIndex: number, dateIndex: number, value: boolean) => void;
  onToggleAll: (roomIndex: number, value: boolean) => void;
  onRoomTypeChange: (roomIndex: number, roomTypeId: string) => void;
  onResolve: (roomIndex: number, dateIndex: number, rateId: string, status: ConflictResolution) => void;
  onUndo: (roomIndex: number, dateIndex: number, rateId: string) => void;
}

export default function StopsellRoomGroupRow({
  roomIndex,
  room,
  selected,
  catalogRooms,
  selectedRoomTypeId,
  resolutions,
  onDateChange,
  onToggleAll,
  onRoomTypeChange,
  onResolve,
  onUndo,
}: Props) {
  const selectedCatalogRoom = useMemo(
    () => catalogRooms.find((c) => c.id === selectedRoomTypeId),
    [catalogRooms, selectedRoomTypeId],
  );

  const blackoutDates = useMemo(() => selectedCatalogRoom?.blackoutDates ?? [], [selectedCatalogRoom]);

  const catalogRoomRates = useMemo(() => selectedCatalogRoom?.roomRates ?? [], [selectedCatalogRoom]);

  const rateConflicts = useMemo(
    () => room.dates.map((date) => catalogRoomRates.map((rate) => findRateConflict(date, rate.id, blackoutDates))),
    [room.dates, catalogRoomRates, blackoutDates],
  );

  const rateResolutions = useMemo(() => sliceResolutionsByRoom(resolutions, roomIndex), [resolutions, roomIndex]);

  const handleChange = useCallback(
    (dateIndex: number, value: boolean) => onDateChange(roomIndex, dateIndex, value),
    [roomIndex, onDateChange],
  );

  const handleToggleAll = useCallback((value: boolean) => onToggleAll(roomIndex, value), [roomIndex, onToggleAll]);

  const handleRoomTypeChange = useCallback(
    (roomTypeId: string) => onRoomTypeChange(roomIndex, roomTypeId),
    [roomIndex, onRoomTypeChange],
  );

  const handleResolve = useCallback(
    (dateIndex: number, rateId: string, status: ConflictResolution) => onResolve(roomIndex, dateIndex, rateId, status),
    [roomIndex, onResolve],
  );

  const handleUndo = useCallback(
    (dateIndex: number, rateId: string) => onUndo(roomIndex, dateIndex, rateId),
    [roomIndex, onUndo],
  );

  return (
    <StopsellRoomGroup
      room={room}
      selected={selected}
      catalogRooms={catalogRooms}
      selectedRoomTypeId={selectedRoomTypeId}
      blackoutDates={blackoutDates}
      catalogRoomRates={catalogRoomRates}
      rateConflicts={rateConflicts}
      rateResolutions={rateResolutions}
      onChange={handleChange}
      onToggleAll={handleToggleAll}
      onRoomTypeChange={handleRoomTypeChange}
      onResolve={handleResolve}
      onUndo={handleUndo}
    />
  );
}
